/* eslint-disable no-param-reassign */
import { Injectable } from '@angular/core';
import {
  StatefulService
} from '@core/services/stateful-service-management/stateful-service-interface/stateful-service-interface';
import { EceApiService } from '@ece/api-services/ece-api.service';
import { DockGroupCardModel } from '@ece-models/dock-group-card.model';
import { PlannerCardThumbnailModel } from '@ece-models/planner-card-thumbnail.model';
import { PlannerGroupThumbnailModel } from '@ece-models/planner-group-thumbnail.model';
import { PlannerModel } from '@ece-models/planner.model';
import { copyObject } from '@shared/zb-object-helper/object-helper';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject } from 'rxjs';
import { v4 } from 'uuid';
import { WeeklyPlannerCardDragService } from './weekly-planner-card-drag.service';

/** Contains state and API calls. */
@Injectable({
  providedIn: 'root'
})
export class WeeklyPlannerService implements StatefulService {
  /**
   * The current weekly planner data.
   */
  plannerData$: BehaviorSubject<PlannerModel> = new BehaviorSubject<PlannerModel>(null);
  /**
   * Emitting true will trigger a refresh of the planner-grid.component
   */
  triggerReloadPlannerGrid$ = new BehaviorSubject<boolean>(false);

  /**
   * Emitting true will trigger a refresh of the side-bar.component
   */
  triggerReloadSideBar$ = new BehaviorSubject<boolean>(false);

  THURSDAY_DAY_OF_WEEK: number = 4;
  FRIDAY_DAY_OF_WEEK: number = 5;
  private sideBarOpen = true;
  okToLoadSidebar = false;
  sidebarWaiting = false;

  createPlannerCardId = (): string => v4();

  set isSideBarOpen(value: boolean) {
    this.sideBarOpen = value;
  }

  get isSideBarOpen(): boolean {
    return this.sideBarOpen;
  }

  // Resize Handlers
  handleRightStretch(
    activeThumbnail: PlannerCardThumbnailModel,
    plannerGroup: PlannerGroupThumbnailModel,
    thumbnailLastDayOfWeek: number): PlannerCardThumbnailModel[] {

    // stop at thursday - trim off extra days for stretched cards being added to a days later in the week
    if (thumbnailLastDayOfWeek > this.THURSDAY_DAY_OF_WEEK) {
      thumbnailLastDayOfWeek = this.THURSDAY_DAY_OF_WEEK;
    }

    // remove any thumbnails that may have been completely or partially covered
    const thumbnailsResizedOver = plannerGroup.plannerCardThumbnails.filter(
      thumbnail => thumbnail.dayOfWeek > activeThumbnail.dayOfWeek && thumbnail.dayOfWeek <= thumbnailLastDayOfWeek);

    if (thumbnailsResizedOver?.length) {
      this.removeThumbnailsResizedOverOnRightStretch(
        activeThumbnail,
        plannerGroup,
        thumbnailsResizedOver,
        thumbnailLastDayOfWeek
      );
    }

    this.addThumbnailsOnRightStretch(activeThumbnail, plannerGroup, thumbnailLastDayOfWeek);

    this.cleanupCardActionRequestBody(activeThumbnail);
    return plannerGroup.plannerCardThumbnails;
  }

  handleLeftStretch(activeThumbnail: PlannerCardThumbnailModel,
    plannerGroup: PlannerGroupThumbnailModel,
    originalStartingDay: number,
    newStartingDay: number): PlannerCardThumbnailModel[] {

    const existingCardOnNewStartingDay = plannerGroup.plannerCardThumbnails
      .find(thumbnail => thumbnail.dayOfWeek === newStartingDay);

    const thumbnailsResizedOver = this.findThumbnailsResizedOverOnLeftStretch(
      activeThumbnail,
      plannerGroup,
      newStartingDay,
      originalStartingDay
    );

    // remove any thumbnails that may have been completely or partially covered
    if (thumbnailsResizedOver?.length) {
      this.removeThumbnailsResizedOverOnLeftStretch(plannerGroup, thumbnailsResizedOver);
    }

    // add new days
    this.addThumbnailsToPlannerGroupOnLeftStretch(
      activeThumbnail,
      plannerGroup,
      existingCardOnNewStartingDay,
      originalStartingDay,
      newStartingDay
    );

    return plannerGroup.plannerCardThumbnails;
  }

  removeDaysFromPlannerGroupForRightReduction(
    plannerGroup: PlannerGroupThumbnailModel, currentThumbnail: PlannerCardThumbnailModel,
    newLastDayOfWeekForThumbnail: number, originalThumbnailWidth: number
  ) {
    plannerGroup.plannerCardThumbnails = plannerGroup.plannerCardThumbnails
      .filter((thumbnail) => {
        // eslint-disable-next-line max-len
        const isThumbnailBeforeOrOnNewLastDay = thumbnail.dayOfWeek <= currentThumbnail.dayOfWeek + originalThumbnailWidth - 1;
        // eslint-disable-next-line max-len
        const isDayWithinOriginalCardWidth = thumbnail.dayOfWeek >= currentThumbnail.dayOfWeek && isThumbnailBeforeOrOnNewLastDay;
        if (isDayWithinOriginalCardWidth) {
          // since the thumbnail is within the original width, return false if it is outside of the new width
          if (thumbnail.dayOfWeek > newLastDayOfWeekForThumbnail) {
            return false;
          }
        }
        return true;
      });

    return plannerGroup.plannerCardThumbnails;
  }

  removeDaysFromPlannerGroupForLeftReduction(
    plannerGroup: PlannerGroupThumbnailModel,  originalThumbnailDayOfWeek: number, newStartingDay: number
  ) {
    for (let i = originalThumbnailDayOfWeek; i < newStartingDay; i++) {
      this.removeDayOfWeekFromGroup(plannerGroup, i);
    }
  }

  private removeThumbnailsResizedOverOnRightStretch(
    activeThumbnail: PlannerCardThumbnailModel, plannerGroup: PlannerGroupThumbnailModel,
    thumbnailsResizedOver: PlannerCardThumbnailModel[], thumbnailLastDayOfWeek: number
  ) {
    thumbnailsResizedOver.forEach((thumbnailResizedOver) => {
      if (thumbnailResizedOver.isMultiDayCard) {
        // delete all next cards
        const thumbnailResizedOverWidth = thumbnailResizedOver.thumbnailWidth;
        const thumbnailResizedOverStartingDayOfWeek = thumbnailResizedOver.dayOfWeek;

        const thumbnailResizedOverEndDay =  thumbnailResizedOver.dayOfWeek + thumbnailResizedOverWidth;
        for (let i = thumbnailResizedOverStartingDayOfWeek + 1; i < thumbnailResizedOverEndDay; i++) {
          this.removeDayOfWeekFromGroup(plannerGroup, i);
        }
      }
      // delete starting card
      this.removeCardFromGroup(plannerGroup, thumbnailResizedOver);
    });

    const daysCovered = thumbnailLastDayOfWeek - activeThumbnail.dayOfWeek + 1;
    for (let dayCovered = daysCovered; dayCovered <= thumbnailLastDayOfWeek; dayCovered++) {
      // check if the next day's card is isPartOfPreviousDayCard, delete it
      const nextDayCard = plannerGroup.plannerCardThumbnails.find(thumbnail => thumbnail.dayOfWeek === dayCovered);

      if (nextDayCard?.isPartOfPreviousDayCard) {
        this.removeCardFromGroup(plannerGroup, nextDayCard);
      }
    }
  }

  private addThumbnailsOnRightStretch(
    activeThumbnail: PlannerCardThumbnailModel, plannerGroup: PlannerGroupThumbnailModel,
    thumbnailLastDayOfWeek: number
  ) {

    for (let i = activeThumbnail.dayOfWeek; i <= thumbnailLastDayOfWeek; i++) {
      let existingDayCardForThumbnail: PlannerCardThumbnailModel = null;

      for (let pct = 0; pct < plannerGroup.plannerCardThumbnails.length; pct++) {
        const isSameCardId =  plannerGroup.plannerCardThumbnails[pct]?.cardId === activeThumbnail.cardId;
        if (isSameCardId && plannerGroup.plannerCardThumbnails[pct]?.dayOfWeek === i) {
          existingDayCardForThumbnail = plannerGroup.plannerCardThumbnails[pct];
        }
      }

      // then add new thumbnails
      if (existingDayCardForThumbnail) {
        const newThumbnails = [];

        for (let pct = 0; pct < plannerGroup.plannerCardThumbnails.length; pct++) {
          if (plannerGroup.plannerCardThumbnails[pct] !== existingDayCardForThumbnail) {
            newThumbnails.push(plannerGroup.plannerCardThumbnails[pct]);
          }
        }

        plannerGroup.plannerCardThumbnails = newThumbnails;
        this.removeCardFromGroup(plannerGroup, existingDayCardForThumbnail);

        plannerGroup.plannerCardThumbnails.push({
          ...activeThumbnail,
          plannerCardId: existingDayCardForThumbnail?.plannerCardId,
          dayOfWeek: i,
        });
      } else if (i <= this.THURSDAY_DAY_OF_WEEK) {
        plannerGroup.plannerCardThumbnails.push({
          ...activeThumbnail,
          dayOfWeek: i,
          plannerCardId: this.createPlannerCardId(),
        });
      }
    }

  }

  private findThumbnailsResizedOverOnLeftStretch(
    activeThumbnail: PlannerCardThumbnailModel, plannerGroup: PlannerGroupThumbnailModel,
    newStartingDay: number, originalStartingDay: number
  ) {
    return plannerGroup.plannerCardThumbnails.filter(
      thumbnail => (thumbnail.dayOfWeek >= newStartingDay)
        && (thumbnail.dayOfWeek < originalStartingDay)
        && (thumbnail.plannerCardId !== activeThumbnail.plannerCardId)
    );
  }

  private removeThumbnailsResizedOverOnLeftStretch(
    plannerGroup: PlannerGroupThumbnailModel,
    thumbnailsResizedOver: PlannerCardThumbnailModel[]
  ) {    // remove existing days
    thumbnailsResizedOver.forEach((thumbnailResizedOver) => {
      this.removeDayOfWeekFromGroup(plannerGroup, thumbnailResizedOver.dayOfWeek);

      if (thumbnailResizedOver.isMultiDayCard) {
        const thumbnailResizedOverStartingDayOfWeek = thumbnailResizedOver.dayOfWeek;

        // check previous days to ensure they're not part of a day that was covered up
        for (let i = thumbnailResizedOverStartingDayOfWeek; i > 0; i--) {
          plannerGroup.plannerCardThumbnails = plannerGroup.plannerCardThumbnails
            .filter(thumbnail => thumbnail.cardId !== thumbnailResizedOver.cardId);
        }
      }
    });
  }

  private addThumbnailsToPlannerGroupOnLeftStretch(
    activeThumbnail: PlannerCardThumbnailModel, plannerGroup: PlannerGroupThumbnailModel,
    existingCardOnNewStartingDay: PlannerCardThumbnailModel, originalStartingDay: number, newStartingDay: number
  ) {
    for (let i = originalStartingDay - 1; i >= newStartingDay; i--) {
      // then add new thumbnails
      if (i === existingCardOnNewStartingDay?.dayOfWeek) {
        plannerGroup.plannerCardThumbnails.push({
          ...activeThumbnail,
          plannerCardId: existingCardOnNewStartingDay?.plannerCardId,
          dayOfWeek: i,
        });
      } else {
        plannerGroup.plannerCardThumbnails.push({
          ...activeThumbnail,
          dayOfWeek: i,
          plannerCardId: this.createPlannerCardId(),
        });
      }
    }
  }

  // Drag & Drop Handlers
  addDaysToDestinationGroupOnDrop(
    activeThumbnail: PlannerCardThumbnailModel,
    plannerGroup: PlannerGroupThumbnailModel,
    lastDayOfThumbnailIndex: number,
    activeThumbnailWidth: number,
    options: any = {}): PlannerCardThumbnailModel[] {
    // stop at thursday - trim off extra days for stretched cards being added to a days later in the week
    if (lastDayOfThumbnailIndex > this.THURSDAY_DAY_OF_WEEK) {
      lastDayOfThumbnailIndex = this.THURSDAY_DAY_OF_WEEK;
    }
    // remove existing day that was dropped on
    const startingDayOfWeek = options.dayOfWeekToUse || activeThumbnail.dayOfWeek;

    this.removeDayOfWeekFromGroup(plannerGroup, startingDayOfWeek);

    // add days for all days covered by thumbnail width
    if (activeThumbnailWidth > 1) {
      this.addDaysOnDrop(activeThumbnail, plannerGroup, activeThumbnailWidth, startingDayOfWeek);
    }

    //  add the new starting day for thumbnail dropped, reuse the plannerCardId from the day it's replacing
    plannerGroup.plannerCardThumbnails.push({
      ...activeThumbnail,
      plannerCardId: options.plannerCardIdToUse || this.createPlannerCardId(),
      dayOfWeek: options.dayOfWeekToUse || activeThumbnail.dayOfWeek,
    });

    return plannerGroup.plannerCardThumbnails;
  }

  private addDaysOnDrop(
    activeThumbnail: PlannerCardThumbnailModel, plannerGroup: PlannerGroupThumbnailModel,
    activeThumbnailWidth: number, startingDayOfWeek: number
  ) {
    for (let i = 1; i < activeThumbnailWidth && i <= this.THURSDAY_DAY_OF_WEEK; i++) {
      // do not add past Thursday (4)
      if (startingDayOfWeek + i <= this.THURSDAY_DAY_OF_WEEK) {
        plannerGroup.plannerCardThumbnails.push({
          ...activeThumbnail,
          dayOfWeek: startingDayOfWeek + i,
          plannerCardId: this.createPlannerCardId(),
        });
      }
    }
  }

  private removePreviousDaysOfStartDayOnDrop(
    plannerGroup: PlannerGroupThumbnailModel, existingCardOnDay: PlannerCardThumbnailModel,
    startingDayOfWeekCardDropped: number
  ) {
    for (let prevDayOfWeek = startingDayOfWeekCardDropped - 1; prevDayOfWeek > 0; prevDayOfWeek--) {
      const prevDay = plannerGroup.weekDays[prevDayOfWeek - 1];
      let partOfCurrent = null;

      if (prevDay) {
        partOfCurrent = this.cardPartOfOtherThumbnail(existingCardOnDay, prevDay.thumbnailStartingDayPlannerCardId);

        if (partOfCurrent) {
          this.removeDayOfWeekFromGroup(plannerGroup, prevDayOfWeek);
        } else {
          // stop checking once previous card is no longer same card
          break;
        }
      }
    }
  }

  private removeNextDaysOfStartingDayCardOnDrop(
    plannerGroup: PlannerGroupThumbnailModel, activeThumbnailWidth: number,
    startingDayOfWeekCardDropped: number, indexOfDayDropped: number
  ) {
    for (let i = startingDayOfWeekCardDropped; i <= this.THURSDAY_DAY_OF_WEEK; i++) {
      const nextDay = plannerGroup.weekDays[i];

      if (nextDay) {
        const cardCovered = nextDay.dayOfWeek <= indexOfDayDropped + activeThumbnailWidth;
        const multiDayShouldBeRemoved = nextDay.isMultiDayCard && nextDay.isPartOfPreviousDayCard;

        if (cardCovered || multiDayShouldBeRemoved) {
          this.removeCardFromGroup(plannerGroup, nextDay);
        } else {
          // stop checking once next card is no longer same card
          break;
        }
      }
    }
  }

  private removeNextDayCardsOnDrop(
    plannerGroup: PlannerGroupThumbnailModel, activeThumbnailWidth: number,
    startingDayOfWeekCardDropped: number, indexOfDayDropped: number
  ) {
    for (let i = startingDayOfWeekCardDropped; i <= this.THURSDAY_DAY_OF_WEEK; i++) {
      // i is index-1 based, weekdays is index-0 based - i gives the next day in the weekDays array
      const nextDay = plannerGroup.weekDays[i];

      if (nextDay) {
        const cardCovered = nextDay.dayOfWeek <= indexOfDayDropped + activeThumbnailWidth;

        this.removeDayOfWeekFromGroup(plannerGroup, i);

        // remove card if covered or card part of a covered card
        if (cardCovered) {
          if (!nextDay.isMultiDayCard) {
            this.removeCardFromGroup(plannerGroup, nextDay);
          } else {
            // Delete all tiles of a multiday card
            for (let c = i; c < i + nextDay.thumbnailWidth; c++) {
              this.removeCardFromGroup(plannerGroup, plannerGroup.weekDays[c]);
            }
          }
        } else {
          // stop checking once next card is not potentially a part of the covered cards
          break;
        }
      }
    }
  }

  removeThumbnailsFromOriginRow(
    plannerCardThumbnail: PlannerCardThumbnailModel, originPlannerGroup: PlannerGroupThumbnailModel,
    destinationPlannerGroup: PlannerGroupThumbnailModel, plannerGroupsToUpdate: PlannerGroupThumbnailModel[],
    indexOfDayDropped: number
  ): string {
    if (originPlannerGroup) {
      this.removeFullThumbnailFromGroupOnDrag(originPlannerGroup, plannerCardThumbnail);
    }

    plannerCardThumbnail = {
      ...plannerCardThumbnail,
      plannerGroupId: destinationPlannerGroup.plannerGroupId,
      dayOfWeek: indexOfDayDropped + 1,
    };
    let originalPlannerCardId: string = null;

    if (this.droppedInSameRow(originPlannerGroup, destinationPlannerGroup)) {
      originalPlannerCardId = originPlannerGroup.plannerCardThumbnails
        .find(thumbnail => thumbnail.dayOfWeek === plannerCardThumbnail.dayOfWeek)?.plannerCardId;
    } else if (this.droppedInDifferentRow(originPlannerGroup, destinationPlannerGroup)) {
      plannerGroupsToUpdate.push(originPlannerGroup);
    }

    return originalPlannerCardId;
  }

  removeDaysFromDestinationGroupOnDrop(
    plannerGroup: PlannerGroupThumbnailModel, indexOfDayDropped: number, activeThumbnailWidth: number
  ) {
    const startingDayOfWeekCardDropped = indexOfDayDropped + 1;

    // weekDays array has 4 entries for each day - if day does not have card, entry is null
    const existingCardOnDay = plannerGroup.weekDays[startingDayOfWeekCardDropped - 1];

    // start with existing day and check all cards
    if (existingCardOnDay) {
      // remove previous cards connected to starting day for card dropped
      this.removePreviousDaysOfStartDayOnDrop(plannerGroup, existingCardOnDay, startingDayOfWeekCardDropped);

      // remove days after thumbnail
      this.removeNextDaysOfStartingDayCardOnDrop(
        plannerGroup,
        activeThumbnailWidth,
        startingDayOfWeekCardDropped,
        indexOfDayDropped
      );

      // remove current day
      this.removeCardFromGroup(plannerGroup, existingCardOnDay);
    } else if (activeThumbnailWidth > 1) {
      // no existing card on dropped day, but activeThumbnail may cover other cards
      this.removeNextDayCardsOnDrop(
        plannerGroup,
        activeThumbnailWidth,
        startingDayOfWeekCardDropped,
        indexOfDayDropped
      );
    }

    return {
      plannerCardThumbnails: plannerGroup.plannerCardThumbnails,
      plannerCardIdToReuse: existingCardOnDay?.plannerCardId || null
    };
  }

  removeFullThumbnailFromGroupOnDrag(
    plannerGroup: PlannerGroupThumbnailModel, thumbnailToRemove: PlannerCardThumbnailModel
  ) {
    this.removeDayOfWeekFromGroup(plannerGroup, thumbnailToRemove.dayOfWeek);

    const lastDay = thumbnailToRemove.dayOfWeek + thumbnailToRemove.thumbnailWidth - 1;
    for (let i = thumbnailToRemove.dayOfWeek; i <= lastDay; i++) {
      plannerGroup.plannerCardThumbnails = plannerGroup.plannerCardThumbnails
        .filter(thumbnail => !this.cardPartOfOtherThumbnail(thumbnail, thumbnailToRemove.plannerCardId));
    }

    return plannerGroup.plannerCardThumbnails;
  }

  cardPartOfOtherThumbnail(thumbnailToCheck: PlannerCardThumbnailModel, idToCheckAgainst: string) {
    return thumbnailToCheck.thumbnailStartingDayPlannerCardId === idToCheckAgainst;
  }

  cleanupCardActionRequestBody(activeThumbnail: PlannerCardThumbnailModel) {
    const unusedByApi = ['isCustomCard', 'isPartOfPreviousDayCard', 'isMultiDayCard', 'thumbnailWidth', 'weekDays'];

    unusedByApi.forEach((prop) => {
      if (activeThumbnail[prop]) {
        delete activeThumbnail[prop];
      }
    });

    return activeThumbnail;
  }

  resizeThumbnailWidth(activeThumbnail: PlannerCardThumbnailModel, dayReleasedOn: number,) : any {
    const currentThumbnailLastDayOfWeek = activeThumbnail.dayOfWeek + (activeThumbnail.thumbnailWidth - 1);
    const stillOnSameDays = (dayReleasedOn === activeThumbnail.dayOfWeek)
      && (dayReleasedOn === currentThumbnailLastDayOfWeek);

    // only update the thumbnail when not released on the same day it was clicked on
    if (!stillOnSameDays) {
      activeThumbnail.thumbnailWidth = (dayReleasedOn) - (activeThumbnail.dayOfWeek) + 1;
      return { thumbnail: activeThumbnail, resized: true };
    }

    return { thumbnail: activeThumbnail, resized: false };

  }

  /**
   * Creates a card model for the planner from list card data.
   *
   * @param {string} cardId The globally-unique identifier for the card.
   * @param {string} cardTitle The card title.
   * @param {string} topicKey Important. This is NOT a topic key. It should not be considered the same as a real topic
   *                          or topic key. It is its own specification and magic string.
   * @param {number} dayOfWeek The day of the week ID.
   * @param {string} plannerGroupId The globally-unique identifier for the planner group.
   * @param {string} plannerCardIdToReuse The globally-unique identifier for the planner card.
   */
  createPlannerCardThumbnail(
    cardId: string, cardTitle: string, topicKey: string, dayOfWeek: number,
    plannerGroupId: string, plannerCardIdToReuse: string
  ) {
    const plannerCardThumbnail = new PlannerCardThumbnailModel();

    plannerCardThumbnail.cardId = cardId;
    plannerCardThumbnail.cardTitle = cardTitle;
    plannerCardThumbnail.topicKey = topicKey;
    plannerCardThumbnail.dayOfWeek = dayOfWeek;
    plannerCardThumbnail.plannerGroupId = plannerGroupId;
    plannerCardThumbnail.plannerCardId = plannerCardIdToReuse || this.createPlannerCardId();
    plannerCardThumbnail.thumbnailWidth = 1;

    return plannerCardThumbnail;
  }

  removeCardFromGroup(plannerGroup: PlannerGroupThumbnailModel, cardToRemove: PlannerCardThumbnailModel) {
    plannerGroup.plannerCardThumbnails = plannerGroup.plannerCardThumbnails
      .filter(thumbnail => thumbnail !== cardToRemove);
  }

  removeDayOfWeekFromGroup(plannerGroup: PlannerGroupThumbnailModel, dayOfWeekToRemove: number) {
    plannerGroup.plannerCardThumbnails = plannerGroup.plannerCardThumbnails
      .filter(thumbnail => thumbnail.dayOfWeek !== dayOfWeekToRemove);
  }

  private droppedInSameRow(
    originPlannerGroup: PlannerGroupThumbnailModel, destinationPlannerGroup: PlannerGroupThumbnailModel
  ): boolean {
    return originPlannerGroup && originPlannerGroup.plannerGroupId === destinationPlannerGroup.plannerGroupId;
  }

  private droppedInDifferentRow(
    originPlannerGroup: PlannerGroupThumbnailModel, destinationPlannerGroup: PlannerGroupThumbnailModel
  ): boolean {
    return originPlannerGroup && originPlannerGroup.plannerGroupId !== destinationPlannerGroup.plannerGroupId;
  }

  constructor(
    private toastrService: ToastrService,
    private eceApiService: EceApiService,
    private weeklyPlannerCardDragService: WeeklyPlannerCardDragService
  ) {
    addEventListener('resize', () => {
      if (window.innerWidth > 1024) {
        this.sideBarOpen = true;
      }
    });
  }

  handleTouchEnd(dockCard: DockGroupCardModel, elementDroppedOn, event) {
    this.saveLogic(dockCard, elementDroppedOn, event);
  }

  saveLogic(cardDropped: DockGroupCardModel, elementDroppedOn: HTMLElement, event) {
    // The element might be a child of the grid-item, make sure we have the correct element
    let element;
    if (elementDroppedOn.classList.contains('grid-item')) {
      element = elementDroppedOn;
    } else {
      element = elementDroppedOn.closest('.grid-item');
    }

    if (element) {
      const planner: PlannerModel = this.plannerData$.getValue();
      let plannerGroupsToUpdate: PlannerGroupThumbnailModel[] = [];
      const groupIdDroppedOn = element.getAttribute('group-id');
      const indexOfDayDroppedOn = this.weeklyPlannerCardDragService
        .dayMouseReleasedOn(event.changedTouches[0].clientX, groupIdDroppedOn);
      const isFriday = element.classList.contains('friday-container');
      const cardId = cardDropped.dockLessonCardId;
      const { sanitizedTitle } = cardDropped;
      const { topicKey } = cardDropped;

      if (isFriday) {
        // Save new friday card from dock
        this.saveNewFridayCard(planner, element.getAttribute('weight'), cardId, sanitizedTitle, topicKey);
      } else {
        // Save new monday through thursday card from dock
        plannerGroupsToUpdate = this
          .saveNewWeekdayCard(indexOfDayDroppedOn, cardId, sanitizedTitle, topicKey, groupIdDroppedOn);

        this.savePlanner(this.plannerData$.value.weekId, plannerGroupsToUpdate);
      }
    }
  }

  saveNewWeekdayCard(
    indexOfDayDroppedOn, dockLessonCardId, sanitizedTitle, topicKey, groupId
  ): PlannerGroupThumbnailModel[] {
    const planner: PlannerModel = this.plannerData$.getValue();
    const plannerGroupsToUpdate: PlannerGroupThumbnailModel[] = [];
    let plannerCardThumbnail: PlannerCardThumbnailModel;

    // eslint-disable-next-line prefer-const
    plannerCardThumbnail = this.createPlannerCardThumbnail(dockLessonCardId,
      sanitizedTitle,
      topicKey,
      parseInt(indexOfDayDroppedOn, 10) + 1,
      groupId,
      null
    );

    const group = this.plannerData$.value.plannerGroupThumbnails
      .find((thumbnail: PlannerGroupThumbnailModel) => thumbnail.plannerGroupId === groupId);

    // remove existing thumbnail days if exist
    const { plannerCardThumbnails, plannerCardIdToReuse } = this
      .removeDaysFromDestinationGroupOnDrop(group, parseInt(indexOfDayDroppedOn, 10), 1);
    planner.plannerCardThumbnails = plannerCardThumbnails;

    // reuse the plannerCardId if already exists for that dayOfWeek
    if (plannerCardIdToReuse) {
      plannerCardThumbnail.plannerCardId = plannerCardIdToReuse;
    }

    planner.plannerCardThumbnails.push(plannerCardThumbnail);
    plannerGroupsToUpdate.push(group);

    const newPlanner: PlannerModel = copyObject({ ...planner }, PlannerModel);

    this.plannerData$.next(newPlanner);

    return plannerGroupsToUpdate;
  }

  saveNewFridayCard(planner: PlannerModel, weight, cardId, title, topicKey) {
    const plannerGroupsToUpdate: PlannerGroupThumbnailModel[] = [];
    let plannerCardThumbnail: PlannerCardThumbnailModel;
    const fridayCards = planner.plannerCardThumbnails.filter(card => this.isFridayCard(card));

    const existingCard = fridayCards.find(x => x.cardId === cardId);

    // Prevent dropping on the same spot
    if (existingCard?.weight !== weight) {
      plannerCardThumbnail = this
        .createPlannerCardThumbnail(cardId,
          title,
          topicKey,
          5,
          null,
          null
        );

      planner.plannerCardThumbnails.push(plannerCardThumbnail);
      plannerGroupsToUpdate.push(new PlannerGroupThumbnailModel());
      plannerGroupsToUpdate[0].weight = weight;
      plannerGroupsToUpdate[0].plannerCardThumbnails.push(plannerCardThumbnail);

      this.savePlanner(planner.weekId, plannerGroupsToUpdate);
    }
  }

  saveExistingFridayCard(activeThumbnailPlannerCardId, originPlannerGroupId, planner, cardId, weight) {
    // card is from planner
    const plannerGroupsToUpdate: PlannerGroupThumbnailModel[] = [];
    let plannerCardThumbnail: PlannerCardThumbnailModel;
    const fridayCards = planner.plannerCardThumbnails.filter(card => this.isFridayCard(card));
    const existingCard = fridayCards.find(x => x.cardId === cardId);

    if (existingCard?.weight !== weight) {
      plannerCardThumbnail = planner.plannerCardThumbnails
        .find(plannerCard => plannerCard.plannerCardId === activeThumbnailPlannerCardId);

      plannerCardThumbnail.thumbnailWidth = 1;

      const originPlannerGroup = planner.plannerGroupThumbnails
        .find(group => group.plannerGroupId === originPlannerGroupId);

      // remove thumbnails from origin group
      if (originPlannerGroup) {
        this.removeFullThumbnailFromGroupOnDrag(originPlannerGroup, plannerCardThumbnail);
      }

      plannerCardThumbnail = {
        ...plannerCardThumbnail,
        plannerGroupId: null,
        dayOfWeek: 5,
      };

      planner.plannerCardThumbnails.push(plannerCardThumbnail);
      plannerGroupsToUpdate.push(new PlannerGroupThumbnailModel());
      plannerGroupsToUpdate[0].weight = weight;
      plannerGroupsToUpdate[0].plannerCardThumbnails.push(plannerCardThumbnail);

      // Remove existing card from either friday or mon-thur
      if (existingCard) {
        plannerGroupsToUpdate.push(new PlannerGroupThumbnailModel());
        plannerGroupsToUpdate[1].weight = existingCard.weight;
      } else {
        plannerGroupsToUpdate.push(originPlannerGroup);
      }

      this.savePlanner(planner.weekId, plannerGroupsToUpdate);
    }
  }

  saveMovedWeekdayPlannerCard(
    activeThumbnailPlannerCardId, activeThumbnailWidth, originPlannerGroupId,
    planner, indexOfDayDropped, destinationPlannerGroup,
    indexOfDayDroppedOn
  ): PlannerGroupThumbnailModel[] {
    const plannerGroupsToUpdate: PlannerGroupThumbnailModel[] = [];
    const plannerCardThumbnail = planner.plannerCardThumbnails
      .find(plannerCard => plannerCard.plannerCardId === activeThumbnailPlannerCardId);

    plannerCardThumbnail.thumbnailWidth = activeThumbnailWidth;

    const originPlannerGroup = planner.plannerGroupThumbnails
      .find(group => group.plannerGroupId === originPlannerGroupId);

    // prevent error when the card is dropped onto the same spot
    const isSameSpot = indexOfDayDropped + 1 === plannerCardThumbnail.dayOfWeek
      && destinationPlannerGroup.plannerGroupId === originPlannerGroup.plannerGroupId;

    if (isSameSpot) {
      return null;
    }

    // removed days being replaced in destination row
    const { plannerCardThumbnails, plannerCardIdToReuse } = this
      .removeDaysFromDestinationGroupOnDrop(destinationPlannerGroup, indexOfDayDroppedOn, activeThumbnailWidth);
    planner.plannerCardThumbnails = plannerCardThumbnails;

    // remove thumbnails from origin group
    let originalPlannerCardId = this
      .removeThumbnailsFromOriginRow(
        plannerCardThumbnail,
        originPlannerGroup,
        destinationPlannerGroup,
        plannerGroupsToUpdate,
        indexOfDayDropped
      );

    // This is for removing a Friday card
    if (!originPlannerGroup) {
      plannerGroupsToUpdate.push(new PlannerGroupThumbnailModel());
      plannerGroupsToUpdate[0].weight = plannerCardThumbnail.weight;
    }

    // add days in destination row from new card
    const lastDayOfThumbnailIndex = (plannerCardThumbnail.dayOfWeek + activeThumbnailWidth - 1);
    // reuse the plannerCardId if already exists for drop day in destination's dayOfWeek
    originalPlannerCardId = plannerCardIdToReuse ?? originalPlannerCardId;

    const options = {
      plannerCardIdToUse: originalPlannerCardId,
      dayOfWeekToUse: indexOfDayDroppedOn + 1
    };

    planner.plannerCardThumbnails = this.addDaysToDestinationGroupOnDrop(
      plannerCardThumbnail,
      destinationPlannerGroup,
      lastDayOfThumbnailIndex,
      activeThumbnailWidth,
      options
    );

    plannerGroupsToUpdate.push(destinationPlannerGroup);
    this.plannerData$.next(planner);

    return plannerGroupsToUpdate;
  }

  isFridayCard(plannerCardThumbnail: PlannerCardThumbnailModel) {
    return plannerCardThumbnail.dayOfWeek === this.FRIDAY_DAY_OF_WEEK;
  }

  savePlanner(weekId: string, plannerGroupsToUpdate: PlannerGroupThumbnailModel[]): any {
    this.eceApiService.updatePlannerGroups(weekId, plannerGroupsToUpdate).subscribe({
      next: (res) => {
        if (res.weekId) {
          return this.triggerReloadPlannerGrid$.next(true);
        }
        return null;
      },
      error: (err) => {
        console.log('Caught error', err);
        this.toastrService.error('An error occurred, refreshing planner.');
        this.triggerReloadPlannerGrid$.next(true);
      }
    });
  }

  /** Resets the stateful properties of this service. */
  reset(): void {
    this.plannerData$.next(null);
    this.triggerReloadPlannerGrid$.next(false);
    this.triggerReloadSideBar$.next(false);
    this.sideBarOpen = true;
    this.okToLoadSidebar = false;
    this.sidebarWaiting = false;
  }
}

/* eslint-enable no-param-reassign */
